/* eslint-disable import/no-extraneous-dependencies */
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker'
import { Dayjs } from 'dayjs'
import { KeyboardArrowDown } from '@mui/icons-material'
import { primaryColors } from '@assets/styles/helpers/colors.styles'
import { datePickerStyles } from '../DatePicker/DatePicker.styles'
import { inputStyles } from '../TextField/TextField.styles'
import { timePickerPopperStyles } from './TimePicker.styles'
import TimePickerActionBar from './TimePickerActionBar'

interface TimePickerProps {
  label: string
  value: Dayjs | null
  onChange: (value: Dayjs | null) => void
  hasError?: boolean
  helperText?: string
  /** Granularity of the minutes column; 15 by default */
  minutesStep?: number
  disabled?: boolean
  /**
   * Earliest / latest time the field accepts. Out-of-range options are greyed
   * out in the clock and typing one marks the field in error, so a bracket the
   * law or the venue imposes does not have to be re-checked by the caller.
   */
  minTime?: Dayjs
  maxTime?: Dayjs
  /**
   * Pins the label to the outline instead of leaving it sitting inside the
   * empty field, where it reads as placeholder text rather than as the field's
   * name.
   *
   * Deliberately not MUI's `focused`, which the app's text fields use for the
   * same effect. On a picker `focused` also paints the outline white through
   * `datePickerStyles`, so the field ends up louder than its neighbours and,
   * being permanently focused, can no longer show where the keyboard actually
   * is. Shrinking the label notches the outline and leaves both alone.
   */
  pinnedLabel?: boolean
}

/**
 * Clock-based time entry, styled to match the app's date range picker, they
 * are the same MUI X family, so the outline, label and popper styles are
 * borrowed rather than re-declared.
 *
 * Always 24-hour: the product is Polish-first and a shift running to 24:00
 * reads badly as 12:00 AM.
 *
 * The columns scroll without a bar of any kind. A visible one costs more than
 * it gives here: the native one takes width off a column barely wider than the
 * numbers in it, and an overlay one is a full-height stripe down a list only a
 * few items tall. Wheel, drag and keyboard all still scroll, and the selected
 * value is scrolled to on open, so there is nothing to hunt for.
 */
function TimePicker(props: TimePickerProps) {
  const {
    label,
    value,
    onChange,
    hasError,
    helperText,
    minutesStep = 15,
    disabled,
    minTime,
    maxTime,
    pinnedLabel
  } = props

  return (
    <MuiTimePicker
      label={label}
      value={value}
      onChange={onChange}
      ampm={false}
      // Both, and they are not the same knob: `minutesStep` is what validation
      // accepts, `timeSteps` is what the clock lists. Passing only the first
      // left the column at MUI's default five minutes with two thirds of the
      // options greyed out.
      minutesStep={minutesStep}
      timeSteps={{ minutes: minutesStep }}
      minTime={minTime}
      maxTime={maxTime}
      disabled={disabled}
      // Typing stays possible; the clock is there for people who would rather
      // not think about the format.
      //
      // Both style sets, in this order: the app's own input styling carries the
      // error look, orange outline and condensed uppercase helper text, the
      // same as every other field, while the picker styles that follow own the
      // MUI X-specific bits. Without the first, MUI's stock red helper text
      // shows up and this is the only field in the app that reports errors in
      // a different colour and typeface.
      sx={{
        ...inputStyles,
        ...datePickerStyles,
        width: '100%',
        // The app's error rules target `.MuiOutlinedInput-notchedOutline`, but
        // MUI X pickers render `.MuiPickersOutlinedInput-notchedOutline`, a
        // different element, so the outline stayed grey while the message
        // below it turned orange. Restated here for the picker's own class.
        '& .Mui-error .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: `${primaryColors.orange} !important`
        },
        '& .MuiFormLabel-root.Mui-error': {
          color: `${primaryColors.orange} !important`
        }
      }}
      slots={{
        openPickerIcon: KeyboardArrowDown,
        actionBar: TimePickerActionBar
      }}
      slotProps={{
        popper: { sx: timePickerPopperStyles },
        // The clock's options are MUI MenuItems; this is the only way to reach
        // them, and the app uses no ripple anywhere.
        digitalClockSectionItem: { disableRipple: true },
        openPickerButton: { disableRipple: true },
        textField: {
          error: hasError,
          helperText,
          size: 'small',
          // Spread, never a plain key: MUI merges these over the ones the
          // picker sets itself, so an `InputLabelProps: undefined` would wipe
          // out whatever the picker had put there.
          ...(pinnedLabel ? { InputLabelProps: { shrink: true } } : {})
        }
      }}
    />
  )
}

export default TimePicker
